'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { getElapsedSecondsOnPage, type ChatContext } from '@/lib/chat-context'
import { getAIReferrerSource } from '@/lib/analytics/ai-referrer-tracker'
import {
  CHAT_FAILURE_MESSAGE,
  parseChatResponse,
} from '@/lib/chat/chat-response'

export type ChatMode = 'sales' | 'trivia'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp?: number
  id?: string
  error?: boolean
  retryable?: boolean
}

export const MAX_MESSAGE_LENGTH = 2000
const RATE_LIMIT_SECONDS = 3

/** Compile-time exhaustiveness: a new response kind or error code must be handled. */
function assertNever(value: never): never {
  throw new Error(`Unhandled /api/chat response: ${JSON.stringify(value)}`)
}

/** Shared media-query hook — also drives the placeholder typewriter in the shell. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return reduced
}

// Context-aware loading messages based on user input
function getContextualLoadingMessages(userMessage: string): string[] {
  const msg = userMessage.toLowerCase().trim()
  const originalMsg = userMessage.trim()

  // TIER 2: Extreme hostility (brief, neutral, static response)
  const profanityCount = (msg.match(/fuck|shit|ass|bitch|bastard|damn/gi) || [])
    .length
  const isAllCaps =
    originalMsg === originalMsg.toUpperCase() && originalMsg.length > 10
  const hasLegalThreat = msg.match(
    /sue|lawyer|attorney|legal action|report you/
  )
  const hasProfanity = msg.match(/fuck|shit|ass|bitch|bastard/)
  const hasPersonalAttack = msg.match(/stupid|idiot|dumb|moron|clown/)

  const isExtremelyHostile =
    profanityCount >= 2 ||
    (isAllCaps && msg.match(/scam|fraud|terrible|worst/)) ||
    (hasLegalThreat && hasProfanity) ||
    (hasPersonalAttack && hasProfanity)

  if (isExtremelyHostile) {
    return ['One moment...']
  }

  // TIER 1: Mild concern/frustration (empathetic, rotating messages)
  if (
    msg.match(
      /scam|fraud|report|lawyer|attorney|sue|legal|authorities|terrible|waste|disappointed|concerned|worried|unsure|skeptical/
    )
  ) {
    return [
      'Processing your feedback...',
      'Reviewing your concern...',
      'Consulting our response framework...',
      'Preparing thoughtful response...',
    ]
  }

  // Pricing/budget questions
  if (
    msg.match(
      /price|pricing|cost|budget|expensive|cheap|how much|quote|rate|fee|afford|payment/
    )
  ) {
    return [
      'Calculating options for your situation...',
      'Preparing pricing details...',
      'Reviewing available packages...',
      'Matching solutions to your budget...',
    ]
  }

  // Low engagement (single word or very short)
  if (
    msg.length < 10 ||
    msg.match(
      /^(hi|hello|hey|yo|sup|ok|okay|yes|no|sure|thanks|thank you|bye)$/i
    )
  ) {
    return [
      'Understanding your question...',
      'Gathering context...',
      'Preparing response...',
      'Analyzing your needs...',
    ]
  }

  // Detailed questions (high-value leads - long, thoughtful messages)
  if (
    msg.length > 50 &&
    (msg.includes('?') ||
      msg.match(/how|why|what|when|where|which|can you|could you|would you/))
  ) {
    return [
      'Analyzing your requirements...',
      'Matching solutions to your needs...',
      'Preparing personalized insights...',
      'Consulting our expertise...',
    ]
  }

  // Technical/specific questions
  if (
    msg.match(
      /timeline|deadline|process|workflow|design|development|ai|agency|work/
    )
  ) {
    return [
      'Reviewing our process...',
      'Preparing detailed explanation...',
      'Consulting project framework...',
      'Organizing information...',
    ]
  }

  return [
    'Orchestrating response...',
    'Analyzing context...',
    'Synthesizing information...',
    'Optimizing response...',
  ]
}

function getAiReferrerSource(): string | undefined {
  // Delegates to the tracker rather than reading storage directly. Reading the
  // key here is what broke attribution when the medium changed, and it also
  // bypassed the consent gate that now lives on the read path.
  return getAIReferrerSource()
}

function getVisitorId(): string {
  let visitorId = localStorage.getItem('pixelmojo_visitor_id')
  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    localStorage.setItem('pixelmojo_visitor_id', visitorId)
  }
  return visitorId
}

interface UseVectorChatOptions {
  chatContext: ChatContext | null
  chatMode: ChatMode
}

/**
 * useVectorChat — the data layer of the Vector chat, extracted from the
 * retired ChatWindow.tsx. Owns session lifecycle, messages, the single
 * canonical send path, retry, rate limits, message limits, session expiry,
 * localStorage persistence, and the answer typewriter reveal. Presentation
 * lives in AskPixelmojo.tsx.
 */
export function useVectorChat({ chatContext, chatMode }: UseVectorChatOptions) {
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLimitReached, setIsLimitReached] = useState(false)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [countdown, setCountdown] = useState(5)
  const [rateLimitCooldown, setRateLimitCooldown] = useState<number | null>(
    null
  )
  const [sendingMessageId, setSendingMessageId] = useState<string | null>(null)
  const [sentMessageIds, setSentMessageIds] = useState<Set<string>>(new Set())
  const [loadingMessages, setLoadingMessages] = useState<string[]>([])
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)

  // Typewriter reveal of the latest assistant answer
  const [typewriterMessageId, setTypewriterMessageId] = useState<string | null>(
    null
  )
  const [displayedContent, setDisplayedContent] = useState('')
  const fullContentRef = useRef('')
  const typewriterIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const sessionPromiseRef = useRef<Promise<string | null> | null>(null)
  const prefersReducedMotion = usePrefersReducedMotion()

  // ---- Session -------------------------------------------------------------

  const ensureSession = useCallback(async (): Promise<string | null> => {
    if (sessionId) return sessionId
    if (sessionPromiseRef.current) return sessionPromiseRef.current

    sessionPromiseRef.current = (async () => {
      try {
        const existingSessionId = localStorage.getItem(
          'pixelmojo_chat_session_id'
        )
        if (existingSessionId) {
          setSessionId(existingSessionId)
          return existingSessionId
        }

        const response = await fetch('/api/chat/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitor_id: getVisitorId(),
            session_metadata: {
              userAgent: navigator.userAgent,
              referrer: document.referrer,
              url: window.location.href,
            },
          }),
        })
        const data = await response.json()
        if (!data.sessionId) {
          sessionPromiseRef.current = null
          return null
        }
        localStorage.setItem('pixelmojo_chat_session_id', data.sessionId)
        setSessionId(data.sessionId)
        return data.sessionId as string
      } catch (error) {
        console.error('Failed to initialize chat session:', error)
        sessionPromiseRef.current = null
        return null
      }
    })()

    return sessionPromiseRef.current
  }, [sessionId])

  // ---- Message loading / persistence ---------------------------------------

  const fetchMessagesFromDB = useCallback(
    async (sid: string, hasCachedMessages: boolean) => {
      try {
        const visitorId = localStorage.getItem('pixelmojo_visitor_id') || ''
        const response = await fetch(
          `/api/chat/messages?sessionId=${sid}&visitorId=${encodeURIComponent(visitorId)}`
        )
        const data = await response.json()
        if (data.messages && data.messages.length > 0) {
          const dbMessages = data.messages.map(
            (msg: { role: string; content: string }, idx: number) => ({
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
              timestamp: Date.now() - (data.messages.length - idx) * 1000,
              id: `${idx}`,
            })
          )
          setMessages(dbMessages)
        } else if (!hasCachedMessages) {
          localStorage.removeItem(`pixelmojo_chat_messages_${sid}`)
        }
      } catch (error) {
        console.error('Failed to fetch messages from DB:', error)
      }
    },
    []
  )

  // Load cached conversation as soon as the session id is known
  useEffect(() => {
    if (!sessionId) return
    try {
      const cached = localStorage.getItem(
        `pixelmojo_chat_messages_${sessionId}`
      )
      if (cached) {
        const parsed = JSON.parse(cached) as ChatMessage[]
        const withMeta = parsed.map((msg, idx) => ({
          ...msg,
          timestamp: msg.timestamp || Date.now() - (parsed.length - idx) * 1000,
          id: msg.id || `${idx}`,
        }))
        setMessages(withMeta)
        fetchMessagesFromDB(sessionId, true)
        return
      }
      fetchMessagesFromDB(sessionId, false)
    } catch (error) {
      console.error('Failed to load messages:', error)
    }
  }, [sessionId, fetchMessagesFromDB])

  // Persist to localStorage whenever messages change
  useEffect(() => {
    if (sessionId && messages.length > 0) {
      localStorage.setItem(
        `pixelmojo_chat_messages_${sessionId}`,
        JSON.stringify(messages)
      )
    }
  }, [messages, sessionId])

  // ---- Typewriter reveal ----------------------------------------------------

  useEffect(() => {
    if (!typewriterMessageId) return

    if (prefersReducedMotion) {
      setDisplayedContent(fullContentRef.current)
      setTypewriterMessageId(null)
      return
    }

    if (typewriterIntervalRef.current) {
      clearInterval(typewriterIntervalRef.current)
    }

    typewriterIntervalRef.current = setInterval(() => {
      const fullContent = fullContentRef.current
      setDisplayedContent(prev => {
        if (prev.length >= fullContent.length) {
          if (typewriterIntervalRef.current) {
            clearInterval(typewriterIntervalRef.current)
            typewriterIntervalRef.current = null
          }
          setTypewriterMessageId(null)
          return fullContent
        }
        const charsToAdd = Math.min(4, fullContent.length - prev.length)
        return fullContent.slice(0, prev.length + charsToAdd)
      })
    }, 12)

    return () => {
      if (typewriterIntervalRef.current) {
        clearInterval(typewriterIntervalRef.current)
      }
    }
  }, [typewriterMessageId, prefersReducedMotion])

  // ---- Loading message rotation ----------------------------------------------

  useEffect(() => {
    if (!isLoading || loadingMessages.length <= 1) return
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length)
    }, 1200)
    return () => clearInterval(interval)
  }, [isLoading, loadingMessages.length])

  // ---- Rate limit cooldown ----------------------------------------------------

  useEffect(() => {
    if (rateLimitCooldown === null || rateLimitCooldown <= 0) return
    const timer = setInterval(() => {
      setRateLimitCooldown(prev =>
        prev === null || prev <= 1 ? null : prev - 1
      )
    }, 1000)
    return () => clearInterval(timer)
  }, [rateLimitCooldown])

  // ---- Session expired countdown → reload -------------------------------------

  useEffect(() => {
    if (!sessionExpired) return
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          window.location.reload()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [sessionExpired])

  // ---- The single canonical send path -----------------------------------------

  const send = useCallback(
    async (rawMessage: string) => {
      const userMessage = rawMessage.trim().slice(0, MAX_MESSAGE_LENGTH)
      if (!userMessage || isLoading || isLimitReached) return
      if (rateLimitCooldown !== null && rateLimitCooldown > 0) return

      setLoadingMessages(getContextualLoadingMessages(userMessage))
      setLoadingMessageIndex(0)
      setIsLoading(true)

      const messageId = `user-${Date.now()}`
      setMessages(prev => [
        ...prev,
        {
          role: 'user',
          content: userMessage,
          timestamp: Date.now(),
          id: messageId,
        },
      ])
      setSendingMessageId(messageId)

      const appendAssistant = (
        content: string,
        extra: Pick<ChatMessage, 'id' | 'error' | 'retryable'> = {}
      ) =>
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content,
            timestamp: Date.now(),
            id: `${extra.error ? 'error' : 'assistant'}-${Date.now()}`,
            ...extra,
          },
        ])

      try {
        const sid = await ensureSession()
        if (!sid) throw new Error('No session')

        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sid,
            message: userMessage,
            messages: messages,
            // timeOnPage is stamped fresh at send time — the widget no
            // longer runs a 1s interval to keep it current.
            chatContext: chatContext
              ? { ...chatContext, timeOnPage: getElapsedSecondsOnPage() }
              : chatContext,
            chatMode: chatMode,
            aiReferrerSource: getAiReferrerSource(),
          }),
        })

        // Refusals arrive with 4xx/5xx statuses but still carry a contract
        // body, so the body (not the status) decides what the visitor sees.
        const result = parseChatResponse(await response.json())
        if (!result) {
          throw new Error(`Unexpected /api/chat response (${response.status})`)
        }

        setSendingMessageId(null)

        switch (result.kind) {
          case 'reply': {
            setSentMessageIds(prev => new Set(prev).add(messageId))
            const assistantMessageId = `assistant-${Date.now()}`
            fullContentRef.current = result.text
            setDisplayedContent('')
            setTypewriterMessageId(assistantMessageId)
            appendAssistant(result.text, { id: assistantMessageId })
            return
          }
          case 'silent':
            // Hard-stopped session: received and deliberately unanswered.
            // Silence vs a closing line is FU-3 in
            // docs/vector-lead-gate-followups.md, still a product decision.
            setSentMessageIds(prev => new Set(prev).add(messageId))
            return
          case 'error':
            switch (result.error) {
              case 'session_not_found':
                localStorage.removeItem('pixelmojo_chat_session_id')
                setSessionExpired(true)
                return
              case 'rate_limit_exceeded':
              case 'message_limit_reached':
                appendAssistant(result.message)
                setRateLimitCooldown(RATE_LIMIT_SECONDS)
                if (result.error === 'message_limit_reached') {
                  setIsLimitReached(true)
                }
                return
              case 'ai_disabled':
              case 'ip_unresolved':
              case 'missing_fields':
              case 'internal_error':
                appendAssistant(result.message, {
                  error: true,
                  retryable: true,
                })
                return
              case 'message_too_long':
              case 'visitor_blocked':
              case 'spam_detected':
                appendAssistant(result.message, {
                  error: true,
                  retryable: false,
                })
                return
              default:
                return assertNever(result.error)
            }
          default:
            return assertNever(result)
        }
      } catch (error) {
        console.error('Failed to send message:', error)
        setSendingMessageId(null)
        appendAssistant(CHAT_FAILURE_MESSAGE, { error: true, retryable: true })
      } finally {
        setIsLoading(false)
      }
    },
    [
      isLoading,
      isLimitReached,
      rateLimitCooldown,
      ensureSession,
      messages,
      chatContext,
      chatMode,
    ]
  )

  const retry = useCallback(
    async (messageIndex: number) => {
      const userMsg = messages[messageIndex - 1]
      if (!userMsg || userMsg.role !== 'user') return
      setMessages(prev => prev.filter((_, i) => i !== messageIndex))
      await send(userMsg.content)
    },
    [messages, send]
  )

  return {
    sessionId,
    ensureSession,
    messages,
    send,
    retry,
    isLoading,
    loadingMessage: isLoading
      ? (loadingMessages[loadingMessageIndex] ?? 'Thinking...')
      : null,
    isLimitReached,
    rateLimitCooldown,
    sessionExpired,
    countdown,
    sendingMessageId,
    sentMessageIds,
    typewriterMessageId,
    displayedContent,
  }
}
